import { UppyFile } from '@uppy/core';
import { useCallback, useRef } from 'react';
import { Button, Center, HelpText, Spinner } from 'src/components/atoms';
import {
  StyledIcon,
  StyledIconInButton,
} from 'src/components/foundations/foundations.styles';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { ALL_MIME_TYPES, FormState } from 'src/types';
import { FileThumbnail } from '../FileThumbnail';
import { FileUploadTooltip } from '../FileUploadTooltip';
import {
  BrowseText,
  DesktopFileTooltip,
  DragAreaEmptyState,
  DragDropText,
  ErrorTextContainer,
  FileList,
  FileSizeText,
  FileUploadContainer,
  FileUploadErrorText,
  InputContainer,
  LimitText,
  LoadingContainer,
  LowerSectionContainer,
  StyledText,
  SupportFilesContainer,
  SupportedFilesContainer,
  UploadButtonContainer,
} from './FileUpload.styles';
import { useFileUploadContext } from './FileUploadContext';
import { FileUploadDropTarget } from './FileUploadDropTarget';
import { AddMoreFilesButtons } from './components/AddMoreFilesButtons';
interface Props {
  dragDropText: string;
  highlightedText: string;
  fileLimitText: string;
  uploadFilesButtonText: string;
  informationText: string;
  addFilesButtonText: string;
  tooltipFileOptions: { type: string; options: string }[];
  confirmButtonText?: string;
  onConfirm?: (files: UppyFile[]) => void;
}

export const FileUpload = ({
  dragDropText,
  highlightedText,
  fileLimitText,
  uploadFilesButtonText,
  informationText,
  addFilesButtonText,
  tooltipFileOptions,
  confirmButtonText,
  onConfirm,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    restrictions,
    handleFileRemove,
    handleFileSelect,
    error,
    files,
    hasUploadNetworkErrors,
    isUploadInFlight,
  } = useFileUploadContext();
  const dropAreaId = 'drag-drop-target';

  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasFiles = files && files.length > 0;

  const showAddMoreButton =
    hasFiles && files.length <= (restrictions?.maxNumberOfFiles ?? 10);

  const openFileSelect = useCallback(() => {
    fileInputRef.current?.click();
  }, [fileInputRef]);

  if (isUploadInFlight) {
    return (
      <Center>
        <LoadingContainer>
          <Spinner
            variant="custom"
            size="large"
            colors={[
              theme.atlas.semantic.color.surface.default,
              theme.atlas.semantic.color.ui.loading.default,
            ]}
          />
          <StyledText>{t('public.Loading.postSubmissionLoading')}</StyledText>
        </LoadingContainer>
      </Center>
    );
  }

  return (
    <FileUploadContainer>
      <FileUploadDropTarget openFileSelect={openFileSelect} id={dropAreaId}>
        <InputContainer>
          <input
            ref={fileInputRef}
            type="file"
            onClick={(e) => {
              if (e.nativeEvent?.target) {
                (e.nativeEvent.target as HTMLInputElement).value = '';
              }
            }}
            onChange={handleFileSelect}
            accept={ALL_MIME_TYPES.join(',')}
            multiple
            data-testid="file-input"
          />
        </InputContainer>
        {hasFiles ? (
          <FileList data-testid="file-list">
            {files.map((file: UppyFile) => (
              <FileThumbnail
                file={file}
                handleFileRemove={handleFileRemove}
                key={file.id}
              />
            ))}
          </FileList>
        ) : (
          <>
            <UploadButtonContainer>
              <Button
                variant="primary"
                width="100%"
                onClick={() => fileInputRef.current?.click()}
              >
                <StyledIconInButton
                  size="medium"
                  position="left"
                  className="uil uil-upload"
                />
                {uploadFilesButtonText}
              </Button>
              <SupportFilesContainer>
                <FileSizeText>{fileLimitText}</FileSizeText>
                <FileUploadTooltip
                  informationText={informationText}
                  fileOptions={tooltipFileOptions}
                  dataTestId="file-upload-type-information-mobile"
                />
              </SupportFilesContainer>
            </UploadButtonContainer>

            <DragAreaEmptyState>
              <DragDropText data-testid="drag-drop-text">
                {dragDropText}{' '}
                <BrowseText data-testid="file-upload-browse-link">
                  {highlightedText}
                </BrowseText>
              </DragDropText>
              <LimitText>{fileLimitText}</LimitText>
            </DragAreaEmptyState>
          </>
        )}
        {error && (
          <HelpText
            data-testid="help-text"
            state={FormState.ERROR}
            helpText={error}
          />
        )}
      </FileUploadDropTarget>

      <LowerSectionContainer>
        {hasUploadNetworkErrors && (
          <ErrorTextContainer>
            <StyledIcon
              className={'uil uil-exclamation-octagon'}
              color={theme.atlas.semantic.color.action.destructive.default}
              size="24px"
              data-testid="file-upload-type-information"
            />
            <FileUploadErrorText>
              {t('public.FileUpload.postSubmissionErrorMessage')}
            </FileUploadErrorText>
          </ErrorTextContainer>
        )}
        <SupportedFilesContainer>
          <DesktopFileTooltip
            informationText={informationText}
            fileOptions={tooltipFileOptions}
          />
          {showAddMoreButton && (
            <AddMoreFilesButtons
              addFilesButtonText={addFilesButtonText}
              confirmButtonText={confirmButtonText}
              onConfirm={onConfirm}
              openFileSelect={openFileSelect}
              files={files}
            />
          )}
        </SupportedFilesContainer>
      </LowerSectionContainer>
    </FileUploadContainer>
  );
};
